import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import { DSOK_ORDER_MANAGEMENT_TYPES } from "app/utils/constants/settings";
import * as Yup from "yup";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  fetchDSOKProducts,
  getDotykackaBranch,
} from "app/services/api/management";
import { makeDotykackaRefreshToken } from "app/services/api/integrations";

const FIELD_NAME = "ORDER_MANAGEMENT_FIELD";

export const ORDER_MANAGEMENT_FIELD_SCHEMA = {
  status: "",
  type: "none",
  api: {
    createOrder: "",
    fetchProductsUrl: "",
  },
  // Optional Dotykačka import language mapping (off by default): { "<dotykackaLang>": "<ourLang>" }.
  languageMapping: {
    enabled: false,
    map: {},
  },
};

export const getOrderManagementFieldValidationSchema = (t) => ({
  status: "",
  type: Yup.string().required(t("messages.checkRequiredFields")),
  api: Yup.object().shape({
    // createOrder: Yup.string().required(t("messages.checkRequiredFields")),
    fetchProductsUrl: Yup.string().required(t("messages.checkRequiredFields")),
  }),
});

// moved into component to access local state/setters

const OrderManagementField = ({ handleChange, expanded }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [fetching, setFetching] = useState();
  const [dotykackaBranches, setDotykackaBranches] = useState();
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [dotykackaGenerateTokenUrl, setDotykackaGenerateTokenUrl] =
    useState(""); // url for dotykacka token
  const customerId = values?.customer?.customerId;
  const fetchProductsUrl = DSOK_ORDER_MANAGEMENT_TYPES.find(
    (type) => type.code === values.orderManagement?.type,
  )?.fetchProductsUrl;

  const orderCreateUrl = DSOK_ORDER_MANAGEMENT_TYPES.find(
    (type) => type.code === values.orderManagement?.type,
  )?.orderCreateUrl;

  const handleMakeRefreshToken = async (customerId, beDomain) => {
    const result = await makeDotykackaRefreshToken(customerId, beDomain);
    console.log("result", result);
    if (result?.redirectUrl) {
      setDotykackaGenerateTokenUrl(result?.redirectUrl);
      // window.open(`${result.redirectUrl}`, "_blank");
    }
  };

  const handleCopyDotykackaRedirectUrl = async () => {
    if (!dotykackaGenerateTokenUrl) return;
    try {
      await navigator.clipboard.writeText(dotykackaGenerateTokenUrl);
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopied", {
          defaultValue: "Redirect URL was copied to clipboard",
        }),
        { variant: "success" },
      );
    } catch (_e) {
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopyError", {
          defaultValue: "Could not copy Redirect URL",
        }),
        { variant: "error" },
      );
    }
  };

  const fetchProducts = async (url) => {
    hideDialog();
    setFetching(true);
    const fetchUrl = `${url}${url?.endsWith("/") ? "" : "/"}${
      values.customer.code
    }`;
    try {
      await fetchDSOKProducts(fetchUrl);
      const message = `${t("pages.dsokDetails.fetchProducts")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("pages.dsokDetails.fetchProducts")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
    }
    setFetching(false);
  };

  useEffect(() => {
    if (fetchProductsUrl && !values.orderManagement?.api?.fetchProductsUrl) {
      setFieldValue("orderManagement.api.fetchProductsUrl", fetchProductsUrl);
    }
  }, [
    fetchProductsUrl,
    setFieldValue,
    values.orderManagement?.api?.fetchProductsUrl,
  ]);

  useEffect(() => {
    const shouldFetchBranches =
      values.orderManagement?.type === "dotykacka" &&
      !!values.orderManagement?.api?.branchId &&
      !!customerId;
    if (!shouldFetchBranches) return;

    let isMounted = true;
    (async () => {
      try {
        const result = await getDotykackaBranch(
          customerId,
          values?.customer?.beDomain,
        );
        if (isMounted) {
          setDotykackaBranches(result?.branches);
        }
      } catch (error) {
        console.log("Error fetching Dotykacka branches:", error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    values.orderManagement?.type,
    values.orderManagement?.api?.branchId,
    customerId,
    values?.customer?.beDomain,
    setDotykackaBranches,
  ]);

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.orderManagement")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name="orderManagement.status"
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue("orderManagement.status", e.target.checked)
                    }
                  />
                }
                label={t("pages.screens.status")}
                checked={values.orderManagement?.status}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                select
                required
                InputLabelProps={{ required: false }}
                name="orderManagement.type"
                label={<Label text={t("pages.dsokDetails.type")} required />}
              >
                {DSOK_ORDER_MANAGEMENT_TYPES.map((type, index) => {
                  return (
                    <MenuItem key={index} value={type.code}>
                      {type.company}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
          {orderCreateUrl && (
            <Grid item xs={12} md={6}>
              <Div sx={{ mt: 1, mb: 1 }}>
                <JumboTextField
                  fullWidth
                  name="orderManagement.api.createOrder"
                  label={t("pages.dsokDetails.createOrder")}
                />
              </Div>
            </Grid>
          )}
          {fetchProductsUrl && (
            <Grid item xs={12} md={6} container spacing={2}>
              <Grid item xs={9} md={9}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    required
                    name="orderManagement.api.fetchProductsUrl"
                    label={t("pages.dsokDetails.fetchProductsUrl")}
                  />
                </Div>
              </Grid>
              <Grid item xs={3} md={3}>
                <Div sx={{ mt: 1, mb: 1, height: "100%" }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      boxShadow: "none",
                      height: "75%",
                    }}
                    disabled={fetching}
                    onClick={() => {
                      showDialog({
                        variant: "confirm",
                        title: t("widgets.confirmDialog.areYouSure"),
                        onYes: () =>
                          fetchProducts(
                            values.orderManagement?.api?.fetchProductsUrl,
                          ),
                        onNo: () => hideDialog(),
                      });
                    }}
                  >
                    {t("pages.dsokDetails.fetchProducts")}
                  </Button>
                </Div>
              </Grid>
            </Grid>
          )}
          {values.orderManagement?.type === "storyous" && (
            <>
              <Grid item xs={12} md={6}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name="orderManagement.api.merchantID"
                    label={t("pages.dsokDetails.merchantID")}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={6}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name="orderManagement.api.placeID"
                    label={t("pages.dsokDetails.placeID")}
                  />
                </Div>
              </Grid>
            </>
          )}
          {values.orderManagement?.type === "dotykacka"
            ? handleMakeRefreshToken(
                customerId,
                values?.customer?.beDomain,
              ) && (
                <>
                  <Grid item xs={12} md={12} lg={12}>
                    <Alert variant="outlined" severity="info" sx={{ mt: 1, mb: 1 }}>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          "& li": { typography: "body2" },
                        }}
                      >
                        <Box component="li">
                          {t(
                            "pages.dsokDetails.integrations.dotykacka.redirectUrlHelp",
                          )}
                        </Box>
                        <Box component="li" sx={{ mt: 1 }}>
                          {t(
                            "pages.dsokDetails.integrations.dotykacka.autoGeneratedFieldsHelp",
                          )}
                        </Box>
                      </Box>
                    </Alert>
                  </Grid>
                  {dotykackaGenerateTokenUrl ? (
                    <Grid item xs={12} md={12} lg={12}>
                      <Div
                        sx={{
                          mt: 0,
                          mb: 1,
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={t(
                            "pages.dsokDetails.integrations.dotykacka.redirectUrlLabel",
                            { defaultValue: "Redirect URL" },
                          )}
                          value={dotykackaGenerateTokenUrl}
                          InputProps={{ readOnly: true }}
                        />
                        <Tooltip
                          title={t(
                            "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                            { defaultValue: "Copy" },
                          )}
                        >
                          <IconButton
                            aria-label={t(
                              "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                              { defaultValue: "Copy" },
                            )}
                            size="small"
                            onClick={() => void handleCopyDotykackaRedirectUrl()}
                            sx={{ mt: 1 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Div>
                    </Grid>
                  ) : null}
                  {values.orderManagement?.api?.refreshToken && (
                    <Grid item xs={12} md={12} lg={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ boxShadow: "none" }}
                        disabled={fetching}
                        onClick={async () => {
                          try {
                            setFetching(true);
                            const dotykackaBranches = await getDotykackaBranch(
                              customerId,
                              values?.customer?.beDomain,
                            );
                            setDotykackaBranches(dotykackaBranches?.branches);
                            console.log("Dotykacka branch:", dotykackaBranches);
                          } catch (error) {
                            console.log("Error:", error);
                          } finally {
                            setFetching(false);
                          }
                        }}
                      >
                        {t("pages.dsokDetails.getBranch")}
                      </Button>
                    </Grid>
                  )}
                  {Array.isArray(dotykackaBranches) &&
                    dotykackaBranches.length > 0 && (
                      <Grid item xs={12} md={12} lg={2}>
                        <Div sx={{ mt: 1, mb: 1 }}>
                          <JumboTextField
                            select
                            fullWidth
                            name="orderManagement.api.branchId"
                            label={t("pages.dsokDetails.branch")}
                          >
                            {dotykackaBranches.map((branch, index) => {
                              const isObject =
                                typeof branch === "object" && branch !== null;
                              const value = isObject
                                ? branch.id ||
                                  branch.branchId ||
                                  branch.code ||
                                  branch.name ||
                                  index
                                : branch;
                              const label = isObject
                                ? branch.name ||
                                  branch.label ||
                                  branch.title ||
                                  branch.id ||
                                  String(value)
                                : String(branch);
                              return (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </JumboTextField>
                        </Div>
                      </Grid>
                    )}
                  <Grid item xs={12} md={12} lg={12}></Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name="orderManagement.api.cloudId"
                        label={t("pages.dsokDetails.cloudId")}
                        InputProps={{ readOnly: true }}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name="orderManagement.api.refreshToken"
                        label={t("pages.dsokDetails.refreshToken")}
                        InputProps={{ readOnly: true }}
                      />
                    </Div>
                  </Grid>
                </>
              )
            : null}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default OrderManagementField;
